/**
 * AttendanceAgent — data-enrichment layer for practice/game attendance.
 *
 * Responsibility: fetch the team's attendance rollup + a bounded recent
 * exception log from the DB and inject them as context into helpAgent, then
 * let the LLM answer naturally (e.g. "who has missed the most practices?",
 * "any absences in the last 2 weeks?"). Mirrors statsAgent.ts's pattern —
 * deterministic fetch, then one LLM call to phrase the answer.
 *
 * Reporting only: this agent never encodes a league eligibility threshold.
 * If asked about eligibility it surfaces the raw numbers and says Hustli
 * has no configured rule, offering to check against one the coach supplies.
 */

import { playerAttendanceService, ATTENDANCE_AT_RISK_THRESHOLD } from '../../../services/playerAttendanceService';
import type { AgentRunContext, AgentRunResult, CoachSteveAgent } from './types';
import { runHelpAgent } from './helpAgent';

// ---------------------------------------------------------------------------
// isAttendanceLookupIntent — regex fallback path only (used when router is off).
// ---------------------------------------------------------------------------

const ATTENDANCE_KEYWORDS =
  /\b(attendance|absen(t|ce|ces)|no.?shows?|excused|(in)?eligib(le|ility))\b/i;
// "missed"/"late" alone are ambiguous (missed a pass, running late, a late
// hit, etc.) — only treat them as an attendance signal when a practice/game
// word is also present in the message.
const MISS_OR_LATE_WORD = /\b(miss(ed|es|ing)?|late)\b/i;
const PRACTICE_OR_GAME_WORD = /\b(practice|game)s?\b/i;

export function isAttendanceLookupIntent(text: string): boolean {
  if (ATTENDANCE_KEYWORDS.test(text)) return true;
  return MISS_OR_LATE_WORD.test(text) && PRACTICE_OR_GAME_WORD.test(text);
}

// ---------------------------------------------------------------------------
// Context fetch + formatting
// ---------------------------------------------------------------------------

const RECENT_WINDOW_DAYS = 90;
const RECENT_EXCEPTION_CAP = 150;

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

/**
 * Fetch the team's attendance rollup + recent exception log and format them
 * as a context block the LLM can reason over.
 */
async function buildAttendanceContext(ctx: AgentRunContext): Promise<string | null> {
  try {
    const [summary, recentExceptions] = await Promise.all([
      playerAttendanceService.getTeamAttendanceSummary(ctx.team.id),
      playerAttendanceService.getTeamAttendanceExceptions(
        ctx.team.id,
        daysAgoISO(RECENT_WINDOW_DAYS),
        RECENT_EXCEPTION_CAP
      ),
    ]);

    const disambiguation =
      'IMPORTANT: Attendance (below) is a completely separate system from player health/availability status (Healthy/Questionable/Doubtful/Out, shown elsewhere in this prompt). ' +
      'A player marked "Out" for injury may have perfect practice attendance, and a healthy player may have missed several practices — never infer one from the other. ' +
      'Answer attendance/absence/no-show/late/eligibility questions using ONLY the records below, not the injury status list.';

    if (summary.total_practices + summary.total_games === 0) {
      return [
        '--- ATTENDANCE RECORDS ---',
        disambiguation,
        'No practices or games have been logged with attendance yet for this team — say so plainly rather than substituting injury/availability data.',
        '---',
      ].join('\n');
    }

    const lines: string[] = [
      '--- ATTENDANCE RECORDS ---',
      disambiguation,
      '',
      `Overall team attendance: ${summary.overall_attendance_percentage}% (${summary.total_practices} practice(s), ${summary.total_games} game(s) logged).`,
      '',
      'Per-player season attendance (sorted worst first):',
    ];

    for (const p of summary.players) {
      lines.push(
        `  ${p.first_name} ${p.last_name}${p.number != null ? ` #${p.number}` : ''}: ` +
        `${p.stats.attendance_percentage}% — practices ${p.stats.practices_present}/${p.stats.total_practices}` +
        `, games ${p.stats.games_present}/${p.stats.total_games}` +
        ` (late ${p.stats.practices_late + p.stats.games_late}, no-show ${p.stats.practices_no_show + p.stats.games_no_show}, excused ${p.stats.practices_excused + p.stats.games_excused})` +
        `, last attended ${p.stats.last_attended ?? 'never'}`
      );
    }

    if (summary.at_risk_players.length > 0) {
      lines.push('', `At-risk players (attendance below ${ATTENDANCE_AT_RISK_THRESHOLD}%):`);
      for (const p of summary.at_risk_players) {
        lines.push(`  ${p.first_name} ${p.last_name}${p.number != null ? ` #${p.number}` : ''}: ${p.attendance_percentage}%`);
      }
    } else {
      lines.push('', `No players are below the ${ATTENDANCE_AT_RISK_THRESHOLD}% at-risk threshold.`);
    }

    if (recentExceptions.length > 0) {
      lines.push(
        '',
        `Recent attendance exceptions (last ${RECENT_WINDOW_DAYS} days, newest first` +
        `${recentExceptions.length >= RECENT_EXCEPTION_CAP ? `, capped at ${RECENT_EXCEPTION_CAP}` : ''}):`
      );
      for (const e of recentExceptions) {
        lines.push(
          `  ${e.event_date} · ${e.event_type} · ${e.first_name} ${e.last_name}${e.number != null ? ` #${e.number}` : ''} · ${e.status}`
        );
      }
    } else {
      lines.push('', `No attendance exceptions logged in the last ${RECENT_WINDOW_DAYS} days.`);
    }

    lines.push(
      '',
      'Notes: a player with no exception record for an event is assumed present — only exceptions (late, no_show, excused) are stored.',
      '"late" means they attended but it is derogatory; "excused" means they were absent but it does not count against attendance; "no_show" is an unexcused absence.',
      'Hustli does not have a configured league/organization eligibility rule (e.g. a minimum-attendance threshold). If asked about eligibility, say so and offer to check the numbers above against whatever threshold the coach provides — do not assume or invent one.',
      `A full visual attendance report (trend chart, roster availability) is available in the app at Team → Attendance.`,
      '---'
    );

    return lines.join('\n');
  } catch (err) {
    console.error('[attendanceAgent] Error building attendance context:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Agent definition
// ---------------------------------------------------------------------------

export const attendanceAgent: CoachSteveAgent = {
  name: 'attendance',
  description:
    'Retrieves practice/game attendance records from the database and lets the LLM interpret them. Handles questions about missed practices/games, absences, no-shows, at-risk players, and attendance-eligibility questions (reports data only — never assumes a league eligibility threshold).',
  applicableSports: 'all',
  match: (text) => isAttendanceLookupIntent(text),

  async handle(text, ctx): Promise<AgentRunResult> {
    const attendanceContext = await buildAttendanceContext(ctx);
    return runHelpAgent(text, ctx, attendanceContext ?? undefined);
  },
};
